import {
  Credit,
  DitapEpsg5179TilingSchema,
  UrlTemplateImageryProvider,
} from "@cesium/engine";
import defined from "../../Core/defined.js";
import DeveloperError from "../../Core/DeveloperError.js";
import Resource from "../../Core/Resource.js";

/**
 * 바로e맵 지도 이미지 제공 클래스.</br>
 * Class providing ngii imagery in DiTAP.
 *
 * @alias NgiiMapImageryProvider
 * @constructor
 *
 * @param {object} options 다음과 같은 속성을 가진 객체.</br> Object with the following options:
 * @param {string} options.apiKey 바로e맵에서 발급한 API 키.
 * </br>(API 키 발급처: 'https://map.ngii.go.kr/')
 * </br>The api Key issued by 'https://map.ngii.go.kr/'
 * @param {string} options.mapType 바로e맵 지도 유형.
</br>&nbsp&nbsp&nbsp1. "korean_map"- 일반지도(국문)
</br>&nbsp&nbsp&nbsp2. "english_map"- 일반지도(영문)
</br>&nbsp&nbsp&nbsp3. "chinese_map"- 일반지도(중문)
</br>&nbsp&nbsp&nbsp4. "japanese_map"- 일반지도(일문)
</br>&nbsp&nbsp&nbsp5. "airphoto"- 영상지도
 * </br>The type of Ngii map. (ex: "korean_map", "english_map", "chinese_map", "japanese_map", "airphoto")
 * 
 * @example
  // DitapViewer 생성
  const viewer = new Ditap.DitapViewer("ditapContainer", {
    baseLayerPicker: true,
  });
  
  // DitapBaseLayerPicker 인스턴스 생성
  const ditapBaseLayerPicker = viewer.ditapBaseLayerPicker;

  // DitapBaseLayerPicker 인스턴스 생성
  const ngiiMapImageryProvider = new Ditap.ProviderViewModel({
    name: "Ngii Korea Map",
    iconUrl: Utils.getUrlFromBase64(Ditap.ConstantUrl.NGII_MAP_IMG_URL),
    tooltip: "Ngii Korea Map",
    category: "Other",
    creationFunction: function () {
      return new Ditap.NgiiMapImageryProvider({
        // 발급 받은 clientId 추가 (필수)
        apiKey: apikey,

        // 맵 타입 추가 (필수)
        mapType: "korean_map",
      });
    },
  });

  ditapBaseLayerPicker.viewModel.selectedImagery = ngiiMapImageryProvider;
 *
 */
class NgiiMapImageryProvider extends UrlTemplateImageryProvider {
  constructor(options) {
    //>>includeStart('debug', pragmas.debug);
    if (!defined(options)) {
      throw new DeveloperError("options is required.");
    }
    //>>includeEnd('debug');

    if (!defined(options.mapType) || !isAvaillableMapType(options.mapType)) {
      throw new DeveloperError("options.mapType is required.");
    } else {
      options.mapType = getMapType(options.mapType);
    }

    // vWorld용 리소스 생성
    if (!defined(options.apiKey)) {
      throw new DeveloperError("options.apiKey is required.");
    }

    function isAvaillableMapType(mapType) {
      mapType = String(mapType).trim().toLowerCase();
      let isAvailableMapType = false;
      if (
        mapType === "korean_map" ||
        mapType === "english_map" ||
        mapType === "chinese_map" ||
        mapType === "japanese_map" ||
        mapType === "airphoto"
      ) {
        isAvailableMapType = true;
      }
      return isAvailableMapType;
    }

    function getMapType(mapType) {
      mapType = String(mapType).trim().toLowerCase();
      let availableMapType;
      if (mapType === "korean_map") {
        availableMapType = "korean_map";
      } else if (mapType === "english_map") {
        availableMapType = "english_map";
      } else if (mapType === "chinese_map") {
        availableMapType = "chinese_map";
      } else if (mapType === "japanese_map") {
        availableMapType = "japanese_map";
      } else if (mapType === "airphoto") {
        availableMapType = "AIRPHOTO";
      }
      return availableMapType;
    }

    function padWithZerosIfNecessary(imageryProvider, key, value) {
      if (
        imageryProvider &&
        imageryProvider.urlSchemeZeroPadding &&
        imageryProvider.urlSchemeZeroPadding.hasOwnProperty(key)
      ) {
        const paddingTemplate = imageryProvider.urlSchemeZeroPadding[key];
        if (typeof paddingTemplate === "string") {
          const paddingTemplateWidth = paddingTemplate.length;
          if (paddingTemplateWidth > 1) {
            value =
              value.length >= paddingTemplateWidth
                ? value
                : new Array(
                    paddingTemplateWidth - value.toString().length + 1
                  ).join("0") + value;
          }
        }
      }
      return value;
    }

    function basicMapTag(imageryProvider, x, y, level) {
      const lz = level + 5;
      const newLevel = lz >= 10 ? `L${lz}` : `L0${lz}`;
      return padWithZerosIfNecessary(imageryProvider, "{basicMap}", newLevel);
    }

    function orthoMapTag(imageryProvider, x, y, level) {
      return padWithZerosIfNecessary(imageryProvider, "{orthoMap}", level + 5);
    }

    const customTags = {
      basicMap: basicMapTag,
      orthoMap: orthoMapTag,
    };

    /*
    가능한 맵 타입 
      korean_map
      english_map
      japanese_map
      chinese_map
      AIRPHOTO
    */
    let customResource;
    if (options.mapType === "AIRPHOTO") {
      customResource = new Resource({
        url: `https://map.ngii.go.kr/openapi/proxy/proxyTile.jsp?apikey=${options.apiKey}&URL=http%3A%2F%2F210.117.198.120%3A8081%2Fo2map%2Fservices%3Fapikey%3D04trYP9_xwLAfALjwZ-B8g%26layer%3DAIRPHOTO%26style%3D_null%26tilematrixset%3DNGIS_AIR%26Service%3DWMTS%26Request%3DGetTile%26Version%3D1.0.0%26Format%3Dimage%252Fjpg%26TileMatrix%3D{orthoMap}%26TileCol%3D{x}%26TileRow%3D{y}`,
      });
    } else {
      customResource = new Resource({
        url: `https://map.ngii.go.kr/openapi/Gettile.do?apikey=${options.apiKey}&service=WMTS&request=GetTile&version=1.0.0&layer=${options.mapType}&style=korean&format=image%2Fpng&tilematrixset=korean&TileMatrix={basicMap}&TileCol={x}&TileRow={y}`,
      });
    }

    const resource = customResource;
    options.url = resource;
    options.tilingScheme = new DitapEpsg5179TilingSchema();
    options.tileWidth = 256;
    options.tileHeight = 256;
    options.minimumLevel = 0;
    options.rectangle = options.tilingScheme.rectangle;
    options.hasAlphaChannel = true;
    options.customTags = customTags;
    options.credit = new Credit(
      "© National Geographic Information Institute, CC-BY-ND"
    );

    super(options);
  }

  getTileCredits() {
    return;
  }

  pickFeatures() {
    return;
  }

  // requestImage() {
  //   return;
  // }
}
export default NgiiMapImageryProvider;
